import { useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Box, Button, Heading, Text } from '@percent/lemonade'
import { useCausesPortalAnalytics } from '@percent/cause-dashboard/common/hooks/useCausesDashboardAnalytics/useCausesDashboardAnalytics'
import { useIntroModalState } from '@percent/cause-dashboard/common/hooks/useIntroModalState/useIntroModalState'
import type { GofundmeUserJourneysVariant } from '@percent/cause-dashboard/constants/gofundmeUserJourneys'
import { GfmInterestConfirmationModal } from '@percent/cause-dashboard/common/components/gfmInterest/GfmInterestConfirmationModal'
import { GFM_INTEREST_EVENTS } from '@percent/cause-dashboard/common/components/gfmInterest/gfmInterestEvents'
import type { LocaleKey } from '@percent/cause-dashboard/i18n/config'
import { gfmImages, gfmLogo } from './gfmHomeBannerAssets'
import styles from './GfmHomeBanner.module.scss'

const copyKeys: Record<GofundmeUserJourneysVariant, { title: LocaleKey; description: LocaleKey; cta: LocaleKey }> = {
  supporters: {
    title: 'typography.gfmHomeBanner.supporters.title',
    description: 'typography.gfmHomeBanner.supporters.description',
    cta: 'typography.gfmHomeBanner.supporters.cta'
  },
  tools: {
    title: 'typography.gfmHomeBanner.tools.title',
    description: 'typography.gfmHomeBanner.tools.description',
    cta: 'typography.gfmHomeBanner.tools.cta'
  },
  supporters_b: {
    title: 'typography.gfmHomeBanner.supporters_b.title',
    description: 'typography.gfmHomeBanner.supporters_b.description',
    cta: 'typography.gfmHomeBanner.supporters_b.cta'
  },
  tools_b: {
    title: 'typography.gfmHomeBanner.tools_b.title',
    description: 'typography.gfmHomeBanner.tools_b.description',
    cta: 'typography.gfmHomeBanner.tools_b.cta'
  },
  goodstack_supporters: {
    title: 'typography.gfmHomeBanner.goodstack_supporters.title',
    description: 'typography.gfmHomeBanner.goodstack_supporters.description',
    cta: 'typography.gfmHomeBanner.goodstack_supporters.cta'
  },
  goodstack_tools: {
    title: 'typography.gfmHomeBanner.goodstack_tools.title',
    description: 'typography.gfmHomeBanner.goodstack_tools.description',
    cta: 'typography.gfmHomeBanner.goodstack_tools.cta'
  }
}

type GfmHomeBannerProps = {
  variant: GofundmeUserJourneysVariant
}

export function GfmHomeBanner({ variant }: Readonly<GfmHomeBannerProps>) {
  const { t } = useTranslation()
  const { track } = useCausesPortalAnalytics()
  const { recordGfmInterestRegistered } = useIntroModalState()
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false)
  const hasTrackedView = useRef(false)
  const copy = copyKeys[variant]
  const isGoodstack = variant === 'goodstack_supporters' || variant === 'goodstack_tools'

  useEffect(() => {
    if (hasTrackedView.current) {
      return
    }
    hasTrackedView.current = true
    track(GFM_INTEREST_EVENTS.shown, { variant, element: 'home_banner' })
  }, [track, variant])

  const handleCtaClick = () => {
    recordGfmInterestRegistered()
    track(GFM_INTEREST_EVENTS.ctaClicked, { variant, element: 'home_banner' })
    setIsConfirmationOpen(true)
  }

  return (
    <Box className={`${styles.banner} ${isGoodstack ? styles.bannerGoodstack : ''}`} data-testid="gfm-home-banner">
      <Box className={styles.content}>
        {!isGoodstack && <img src={gfmLogo} alt="GoFundMe" className={styles.logo} />}
        <Heading level="h2" align="center" className={styles.title}>
          {t(copy.title)}
        </Heading>
        <Text size="medium" color="var(--semantic-content-secondary)">
          {isGoodstack ? (
            <Trans
              // @ts-expect-error - i18nKey union type too complex for TS to represent
              i18nKey={copy.description}
              components={{ strong: <strong /> }}
            />
          ) : (
            t(copy.description)
          )}
        </Text>
        <Box className={isGoodstack ? styles.ctaDefault : styles.cta}>
          <Button onClick={handleCtaClick} data-testid="gfm-home-banner-cta">
            {t(copy.cta)}
          </Button>
        </Box>
      </Box>
      <Box className={styles.imageWrapper}>
        <img src={gfmImages[variant]} alt="" className={styles.image} />
      </Box>
      <GfmInterestConfirmationModal
        open={isConfirmationOpen}
        onClose={() => setIsConfirmationOpen(false)}
        isGoodstack={isGoodstack}
      />
    </Box>
  )
}
